import { useMemo, useState } from "react";
import {
  EuiBadge,
  EuiBasicTable,
  EuiButton,
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiFieldSearch,
  EuiFieldText,
  EuiFilterButton,
  EuiFilterGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHealth,
  EuiHorizontalRule,
  EuiIcon,
  EuiLoadingSpinner,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiPanel,
  EuiPopover,
  EuiPopoverTitle,
  EuiProgress,
  EuiSpacer,
  EuiText,
  EuiToolTip,
} from "@elastic/eui";
import { OpsanioVersion, OpsanioVersions } from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import { AnketaResourcesEnum } from "src/auth/types";
import { useOpsanioCan } from "../can";
import { GitLabTagPopover } from "../gitlab";
import {
  ComponentRow,
  ComponentRowState,
  VersionChoice,
  WaveSummary,
  buildComponentRows,
  componentStateColor,
  versionChoicesFor,
} from "./model";

type FilterKey = "outdated" | "all" | "current";

/**
 * Manual version selection for one component.
 *
 * "Bump" only ever writes the newest tag, which leaves every other legitimate
 * move - pinning an older patch, following a floating `vX.Y` channel, putting a
 * single component on `test`, or correcting a value GitLab does not know about
 * - to the wave-wide edit modal, where the field has to be found among 25 free
 * text inputs. The picker offers what GitLab reports for this component plus a
 * free text escape hatch, on the row the value belongs to.
 */
const ComponentVersionPicker = ({
  row,
  choices,
  disabled,
  onPick,
}: {
  row: ComponentRow;
  choices: VersionChoice[];
  disabled: boolean;
  onPick: (value: string) => void;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [custom, setCustom] = useState<string>("");

  const pick = (value: string): void => {
    setIsOpen(false);
    setCustom("");
    onPick(value);
  };

  const kindLabel = (kind: VersionChoice["kind"]): string =>
    kind === "patch"
      ? translate("anketa.opsanio.waves.components.kind.patch", {
          defaultMessage: "release",
        })
      : kind === "minor"
        ? translate("anketa.opsanio.waves.components.kind.minor", {
            defaultMessage: "floating",
          })
        : translate("anketa.opsanio.waves.components.kind.channel", {
            defaultMessage: "channel",
          });

  const setLabel = translate("anketa.opsanio.waves.components.set_version", {
    defaultMessage: "Set version",
  });

  return (
    <EuiPopover
      panelPaddingSize="s"
      isOpen={isOpen}
      closePopover={() => setIsOpen(false)}
      anchorPosition="downRight"
      button={
        <EuiToolTip content={setLabel}>
          <EuiButtonIcon
            size="xs"
            iconType="pencil"
            color="text"
            aria-label={setLabel}
            isDisabled={disabled}
            onClick={() => setIsOpen((open) => !open)}
          />
        </EuiToolTip>
      }
    >
      <div style={{ width: 260 }}>
        <EuiPopoverTitle>{`${setLabel} · ${row.name}`}</EuiPopoverTitle>
        {choices.map((choice) => (
          <EuiFlexGroup
            key={choice.value}
            gutterSize="xs"
            alignItems="center"
            responsive={false}
          >
            <EuiFlexItem>
              <EuiButtonEmpty
                size="xs"
                flush="left"
                iconType={choice.value === row.running ? "check" : "empty"}
                isDisabled={choice.value === row.running}
                onClick={() => pick(choice.value)}
              >
                {choice.value}
              </EuiButtonEmpty>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiBadge color="hollow">{kindLabel(choice.kind)}</EuiBadge>
            </EuiFlexItem>
          </EuiFlexGroup>
        ))}
        {choices.length === 0 && (
          <EuiText size="xs" color="subdued">
            {translate("anketa.opsanio.waves.components.no_tags", {
              defaultMessage: "GitLab reports no tags for this component.",
            })}
          </EuiText>
        )}
        <EuiHorizontalRule margin="s" />
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem>
            <EuiFieldText
              compressed
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder={translate(
                "anketa.opsanio.waves.components.custom_version",
                { defaultMessage: "Custom value" },
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" && custom.trim()) pick(custom.trim());
              }}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton
              size="s"
              isDisabled={!custom.trim() || custom.trim() === row.running}
              onClick={() => pick(custom.trim())}
            >
              {translate("anketa.opsanio.waves.components.apply", {
                defaultMessage: "Apply",
              })}
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>
    </EuiPopover>
  );
};

/**
 * The pinned component set as a dense table.
 *
 * This replaces a stack of one full-width panel per component - 25 of them,
 * nested two panels deep - which made the single most important comparison
 * (what is behind, and behind what) the hardest thing on the page to see.
 * Outdated rows sort first by default and the filter opens on them.
 */
export const WaveComponentTable = ({
  summary,
  parentVersions,
  tags,
  updateWaveVersion,
}: {
  summary: WaveSummary;
  parentVersions: OpsanioVersions | null;
  tags: any;
  updateWaveVersion: (
    wave: OpsanioVersion,
    data: OpsanioVersions,
    versionToBump: keyof OpsanioVersion,
    newVersion: string,
  ) => Promise<void>;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  // Every control here writes the pinned versions: `opsanio/updates · update`.
  const can = useOpsanioCan();
  const canEdit = can(AnketaResourcesEnum.opsanioUpdates, "update");

  const [filter, setFilter] = useState<FilterKey>("outdated");
  const [query, setQuery] = useState<string>("");
  const [sortField, setSortField] = useState<"name" | "state">("state");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [busyField, setBusyField] = useState<string | null>(null);
  const [bulk, setBulk] = useState<{
    kind: "bump" | "sync";
    current: number;
    total: number;
  } | null>(null);
  const [confirm, setConfirm] = useState<"bump" | "sync" | null>(null);

  const rows = useMemo(
    () =>
      buildComponentRows(summary.version, parentVersions, tags, (field) =>
        translate(`anketa.opsanio.field.${String(field)}`, {
          defaultMessage: String(field).toLocaleUpperCase(),
        }),
      ),
    [summary.version, parentVersions, tags, translate],
  );

  const outdated = rows.filter((r) => r.state === "outdated");
  const syncable = rows.filter((r) => r.differsFromParent);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (filter === "outdated" && r.state !== "outdated") return false;
      if (filter === "current" && r.state === "outdated") return false;
      if (needle && !`${r.name} ${String(r.field)}`.toLowerCase().includes(needle))
        return false;
      return true;
    });

    const weight = (s: ComponentRowState): number =>
      s === "outdated" ? 0 : s === "unknown" ? 1 : 2;

    const sorted = [...filtered].sort((a, b) =>
      sortField === "name"
        ? a.name.localeCompare(b.name)
        : weight(a.state) - weight(b.state) || a.name.localeCompare(b.name),
    );
    return sortDirection === "desc" ? sorted.reverse() : sorted;
  }, [rows, filter, query, sortField, sortDirection]);

  /** Write one component's version - both "Bump" and the manual picker. */
  const setOne = async (row: ComponentRow, value: string): Promise<void> => {
    const next = value.trim();
    if (!summary.version || !next || next === row.running) return;
    setBusyField(String(row.field));
    try {
      await updateWaveVersion(
        summary.wave as any,
        summary.version,
        row.field as any,
        next,
      );
    } finally {
      setBusyField(null);
    }
  };

  const bumpOne = (row: ComponentRow): Promise<void> => setOne(row, row.latest);

  /**
   * Apply a whole set of version changes one PUT at a time, each request
   * carrying every change applied so far so the server never sees a partially
   * rolled-back document if the run is interrupted.
   */
  const runBulk = async (kind: "bump" | "sync"): Promise<void> => {
    const targets = kind === "bump" ? outdated : syncable;
    if (!summary.version || targets.length === 0) return;
    setConfirm(null);
    setBulk({ kind, current: 0, total: targets.length });
    try {
      for (let i = 0; i < targets.length; i++) {
        const applied: any = { ...summary.version };
        for (let j = 0; j <= i; j++) {
          applied[targets[j].field] =
            kind === "bump" ? targets[j].latest : targets[j].parent;
        }
        await updateWaveVersion(
          summary.wave as any,
          applied,
          targets[i].field as any,
          kind === "bump" ? targets[i].latest : targets[i].parent,
        );
        setBulk({ kind, current: i + 1, total: targets.length });
      }
    } finally {
      setBulk(null);
    }
  };

  const stateLabel = (state: ComponentRowState): string =>
    state === "outdated"
      ? translate("anketa.opsanio.waves.components.outdated", {
          defaultMessage: "Outdated",
        })
      : state === "current"
        ? translate("anketa.opsanio.waves.components.up_to_date", {
            defaultMessage: "Up to date",
          })
        : translate("anketa.opsanio.waves.components.unknown", {
            defaultMessage: "Unknown",
          });

  const columns = [
    {
      field: "name",
      name: translate("anketa.opsanio.waves.components.column.component", {
        defaultMessage: "Component",
      }),
      sortable: true,
      truncateText: true,
      render: (name: string) => <strong>{name}</strong>,
    },
    {
      field: "running",
      name: translate("anketa.opsanio.waves.components.column.running", {
        defaultMessage: "Running",
      }),
      width: "130px",
      render: (running: string, row: ComponentRow) => (
        <EuiText size="s" color={row.state === "outdated" ? "warning" : "default"}>
          {running || "-"}
        </EuiText>
      ),
    },
    {
      field: "parent",
      name: translate("anketa.opsanio.waves.components.column.parent", {
        defaultMessage: "Parent",
      }),
      width: "130px",
      render: (parent: string, row: ComponentRow) => (
        <EuiText size="s" color={row.differsFromParent ? "accent" : "subdued"}>
          {parent || "-"}
        </EuiText>
      ),
    },
    {
      field: "latest",
      name: translate("anketa.opsanio.waves.components.column.latest", {
        defaultMessage: "Latest tag",
      }),
      width: "150px",
      render: (latest: string, row: ComponentRow) =>
        row.latestTag ? (
          <GitLabTagPopover tag={row.latestTag} />
        ) : (
          <EuiText size="s" color="subdued">
            {latest || "-"}
          </EuiText>
        ),
    },
    {
      field: "state",
      name: translate("anketa.opsanio.waves.components.column.state", {
        defaultMessage: "State",
      }),
      sortable: true,
      width: "130px",
      render: (state: ComponentRowState) => (
        <EuiHealth color={componentStateColor(state)} textSize="xs">
          {stateLabel(state)}
        </EuiHealth>
      ),
    },
    {
      name: translate("anketa.opsanio.waves.components.column.action", {
        defaultMessage: "Action",
      }),
      align: "right" as const,
      width: "150px",
      render: (row: ComponentRow) =>
        busyField === String(row.field) ? (
          <EuiLoadingSpinner size="m" />
        ) : (
          <EuiFlexGroup
            gutterSize="none"
            alignItems="center"
            justifyContent="flexEnd"
            responsive={false}
          >
            {canEdit && row.state === "outdated" && (
              <EuiFlexItem grow={false}>
                <EuiButtonEmpty
                  size="xs"
                  iconType="sortUp"
                  isDisabled={Boolean(bulk)}
                  onClick={() => bumpOne(row)}
                >
                  {translate("anketa.opsanio.waves.components.bump", {
                    defaultMessage: "Bump",
                  })}
                </EuiButtonEmpty>
              </EuiFlexItem>
            )}
            {canEdit && (
            <EuiFlexItem grow={false}>
              <ComponentVersionPicker
                row={row}
                choices={versionChoicesFor(tags, row.field, row.running)}
                disabled={Boolean(bulk)}
                onPick={(value) => setOne(row, value)}
              />
            </EuiFlexItem>
            )}
          </EuiFlexGroup>
        ),
    },
  ];

  const filterButtons: Array<{ key: FilterKey; label: string; count?: number }> = [
    {
      key: "outdated",
      label: translate("anketa.opsanio.waves.components.filter.outdated", {
        defaultMessage: "Outdated",
      }),
      count: outdated.length,
    },
    {
      key: "all",
      label: translate("anketa.opsanio.waves.components.filter.all", {
        defaultMessage: "All",
      }),
      count: rows.length,
    },
    {
      key: "current",
      label: translate("anketa.opsanio.waves.components.filter.current", {
        defaultMessage: "Up to date",
      }),
    },
  ];

  const confirmRows =
    confirm === "bump" ? outdated : confirm === "sync" ? syncable : [];

  return (
    <EuiPanel hasBorder paddingSize="none" hasShadow={false}>
      <EuiPanel color="transparent" paddingSize="m" hasShadow={false}>
        <EuiFlexGroup gutterSize="s" alignItems="center" wrap responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiText size="xs" color="subdued">
              <strong>
                {translate("anketa.opsanio.waves.components", {
                  defaultMessage: "COMPONENTS",
                })}
              </strong>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiFilterGroup>
              {filterButtons.map((f) => (
                <EuiFilterButton
                  key={f.key}
                  hasActiveFilters={filter === f.key}
                  numFilters={f.count}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </EuiFilterButton>
              ))}
            </EuiFilterGroup>
          </EuiFlexItem>
          <EuiFlexItem grow={false} style={{ minWidth: 200 }}>
            <EuiFieldSearch
              compressed
              isClearable
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={translate(
                "anketa.opsanio.waves.components.search",
                { defaultMessage: "Filter components" },
              )}
            />
          </EuiFlexItem>
          <EuiFlexItem />
          {canEdit && syncable.length > 0 && (
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                color="accent"
                iconType="merge"
                isDisabled={Boolean(bulk)}
                onClick={() => setConfirm("sync")}
              >
                {translate("anketa.opsanio.waves.components.sync_parent", {
                  values: { count: syncable.length },
                  defaultMessage: "Sync with parent ({{count}})",
                })}
              </EuiButton>
            </EuiFlexItem>
          )}
          {canEdit && outdated.length > 0 && (
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                fill
                iconType="sortUp"
                isDisabled={Boolean(bulk)}
                onClick={() => setConfirm("bump")}
              >
                {translate("anketa.opsanio.waves.components.bump_all", {
                  values: { count: outdated.length },
                  defaultMessage: "Bump all ({{count}})",
                })}
              </EuiButton>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiPanel>

      {bulk && (
        <EuiPanel color="subdued" paddingSize="s" hasShadow={false} borderRadius="none">
          <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
            <EuiFlexItem>
              <EuiText size="xs" color="subdued">
                {bulk.kind === "bump"
                  ? translate("anketa.opsanio.waves.components.bumping", {
                      defaultMessage: "Bumping components",
                    })
                  : translate("anketa.opsanio.waves.components.syncing", {
                      defaultMessage: "Syncing with parent",
                    })}
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                {bulk.current} / {bulk.total}
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer size="xs" />
          <EuiProgress
            value={bulk.current}
            max={bulk.total}
            size="s"
            color={bulk.kind === "bump" ? "primary" : "accent"}
          />
        </EuiPanel>
      )}

      <EuiHorizontalRule margin="none" />

      <EuiBasicTable
        items={visible}
        itemId="field"
        columns={columns as any}
        compressed
        sorting={{ sort: { field: sortField, direction: sortDirection } }}
        onChange={({ sort }: any) => {
          if (!sort) return;
          setSortField(sort.field === "name" ? "name" : "state");
          setSortDirection(sort.direction);
        }}
        noItemsMessage={
          summary.version
            ? translate("anketa.opsanio.waves.components.empty", {
                defaultMessage: "No components match this filter.",
              })
            : translate("anketa.opsanio.waves.components.unconfigured", {
                defaultMessage: "No version set has been pinned for this wave yet.",
              })
        }
      />

      <EuiPanel color="subdued" paddingSize="s" hasShadow={false} borderRadius="none">
        <EuiText size="xs" color="subdued">
          {translate("anketa.opsanio.waves.components.footer", {
            values: {
              shown: visible.length,
              total: rows.length,
              outdated: outdated.length,
            },
            defaultMessage:
              "{{shown}} of {{total}} shown · {{outdated}} outdated",
          })}
        </EuiText>
      </EuiPanel>

      {confirm && (
        <EuiModal onClose={() => setConfirm(null)} maxWidth={720}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              {confirm === "bump"
                ? translate("anketa.opsanio.waves.components.bump_all", {
                    values: { count: outdated.length },
                    defaultMessage: "Bump all ({{count}})",
                  })
                : translate("anketa.opsanio.waves.components.sync_parent", {
                    values: { count: syncable.length },
                    defaultMessage: "Sync with parent ({{count}})",
                  })}
            </EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            <EuiText size="s">
              {confirm === "bump"
                ? translate("anketa.opsanio.waves.components.bump_all.intro", {
                    defaultMessage: "The following components will be updated:",
                  })
                : translate("anketa.opsanio.waves.components.sync_parent.intro", {
                    defaultMessage:
                      "The following components will be set to the parent wave's version:",
                  })}
            </EuiText>
            <EuiSpacer size="m" />
            {confirmRows.map((row) => (
              <EuiFlexGroup
                key={String(row.field)}
                gutterSize="s"
                alignItems="center"
                responsive={false}
              >
                <EuiFlexItem>
                  <EuiText size="s">{row.name}</EuiText>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiBadge color="hollow">{row.running || "-"}</EuiBadge>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiIcon type="sortRight" size="s" />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiBadge color={confirm === "bump" ? "success" : "accent"}>
                    {confirm === "bump" ? row.latest : row.parent}
                  </EuiBadge>
                </EuiFlexItem>
              </EuiFlexGroup>
            ))}
          </EuiModalBody>
          <EuiModalFooter>
            <EuiButtonEmpty onClick={() => setConfirm(null)}>
              {translate("anketa.opsanio.modal.cancel", {
                defaultMessage: "Abbrechen",
              })}
            </EuiButtonEmpty>
            <EuiButton
              fill
              color={confirm === "bump" ? "primary" : "accent"}
              onClick={() => runBulk(confirm)}
            >
              {translate("anketa.opsanio.waves.components.confirm", {
                defaultMessage: "Confirm",
              })}
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}
    </EuiPanel>
  );
};
